"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFormStates = void 0;
const react_1 = require("react");
const objectField_1 = require("./fields/objectField");
const utils_1 = require("./utils");
function useFormStates(opts) {
    const { config, autoSave, getId, map, addRules, readOnly = false } = opts;
    const objectStateCache = (0, react_1.useMemo)(() => ({}), 
    // Force resetting the cache if config changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config]);
    // Keep track of ObjectStates that triggered auto-save when a save was already in progress.
    const pendingAutoSaves = (0, react_1.useRef)(new Set());
    // Use a ref so our memo'ized `autoSave` always see the latest value
    const autoSaveRef = (0, react_1.useRef)(autoSave);
    autoSaveRef.current = autoSave;
    // Use a ref b/c we're memod
    const readOnlyRef = (0, react_1.useRef)(readOnly);
    readOnlyRef.current = readOnly;
    const getFormState = (0, react_1.useCallback)((input, opts = {}) => {
        const existing = objectStateCache[getId(input)];
        let form = existing === null || existing === void 0 ? void 0 : existing[0];
        async function maybeAutoSave(form) {
            // Don't use canSave() because we don't want to set touched for all the fields
            if (autoSaveRef.current && form.dirty && form.valid) {
                const { current: pending } = pendingAutoSaves;
                if (isAutoSaving) {
                    pending.add(form);
                    return;
                }
                let maybeError;
                try {
                    isAutoSaving = true;
                    // See if we have any reactions that want to run (i.e. added by addRules hooks)
                    await new Promise((resolve) => setTimeout(resolve, 0));
                    // If a reaction re-queued our form during the ^ wait, remove it
                    pending.delete(form);
                    await autoSaveRef.current(form);
                }
                catch (e) {
                    maybeError = String(e);
                    throw e;
                }
                finally {
                    isAutoSaving = false;
                    if (pending.size > 0) {
                        const first = pending.values().next().value;
                        pending.delete(first);
                        await maybeAutoSave(first);
                    }
                }
            }
        }
        // If it didn't exist, then add to the cache.
        if (!form) {
            form = (0, objectField_1.createObjectState)(config, (0, utils_1.initValue)(config, map ? { map, input } : input), {
                maybeAutoSave: () => maybeAutoSave(form),
            });
            if (addRules) {
                addRules(form);
            }
            objectStateCache[getId(input)] = [form, input];
        }
        // If the source of truth changed, then update the existing state and return it.
        if (existing && existing[1] !== input) {
            form.set((0, utils_1.initValue)(config, map ? { map, input } : input), {
                refreshing: true,
            });
            existing[1] = input;
        }
        form.readOnly = readOnlyRef.current || !!opts.readOnly;
        return form;
    }, 
    // Allow the user to not stable-ize getId, map, addRules, and autoSave
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objectStateCache, config]);
    return { getFormState };
}
exports.useFormStates = useFormStates;
// If the user's autoSave hook makes some last-minute `.set` calls to sneak
// in some business logic right before their GraphQL mutation call, ignore it
// so that we don't infinite loop.
let isAutoSaving = false;
